import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowsUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { LoadingSkeleton, EmptyState } from '@repro/ui';
import { Invoice } from '../shared';
import { api } from '../utils/api';

interface InvoiceListProps {
  organisationId: string;
}

type SortKey = 'invoiceNumber' | 'issueDate' | 'dueDate' | 'total';
type SortDirection = 'asc' | 'desc';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'pending':
    case 'sent':
      return 'bg-yellow-100 text-yellow-800';
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'draft':
      return 'bg-blue-100 text-blue-800';
    case 'cancelled':
    case 'refunded':
      return 'bg-base-200 text-base-content/70';
    default:
      return 'bg-base-200 text-base-content/70';
  }
}

function formatDate(value: Date | string | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
}

const InvoiceList: React.FC<InvoiceListProps> = ({ organisationId }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('issueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;
    setLoading(true);
    api
      .getInvoices(organisationId)
      .then((data) => {
        if (!cancelled) setInvoices(data);
      })
      .catch((err) => {
        if (!cancelled) console.error('Error fetching invoices:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const filtered = useMemo(() => {
    let result = [...invoices];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (invoice) =>
          invoice.invoiceNumber.toLowerCase().includes(q) ||
          invoice.billingAddress.companyName.toLowerCase().includes(q) ||
          invoice.purchaseOrderNumber?.toLowerCase().includes(q)
      );
    }

    if (statusFilter.size > 0) {
      result = result.filter((invoice) => statusFilter.has(invoice.status));
    }

    if (dateFrom) {
      const cutoff = new Date(dateFrom).getTime();
      result = result.filter((invoice) => new Date(invoice.issueDate).getTime() >= cutoff);
    }
    if (dateTo) {
      const endOfDay = new Date(dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      const cutoff = endOfDay.getTime();
      result = result.filter((invoice) => new Date(invoice.issueDate).getTime() <= cutoff);
    }

    result.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      switch (sortKey) {
        case 'invoiceNumber':
          av = a.invoiceNumber;
          bv = b.invoiceNumber;
          break;
        case 'issueDate':
          av = new Date(a.issueDate).getTime();
          bv = new Date(b.issueDate).getTime();
          break;
        case 'dueDate':
          av = new Date(a.dueDate).getTime();
          bv = new Date(b.dueDate).getTime();
          break;
        case 'total':
          av = a.total;
          bv = b.total;
          break;
      }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [invoices, searchTerm, statusFilter, dateFrom, dateTo, sortKey, sortDirection]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ArrowsUpDownIcon className="h-3.5 w-3.5 opacity-40" />;
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="h-3.5 w-3.5" />
    ) : (
      <ChevronDownIcon className="h-3.5 w-3.5" />
    );
  };

  const toggleStatus = (value: string) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter(new Set());
    setDateFrom('');
    setDateTo('');
  };

  const filtersActive =
    searchTerm.length > 0 || statusFilter.size > 0 || dateFrom !== '' || dateTo !== '';

  if (!organisationId) {
    return (
      <div className="bg-base-100 shadow rounded-lg p-6">
        <p className="text-error">Please select an organisation to view invoices.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-base-100 shadow rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-base-content">Invoices</h1>
          <button
            type="button"
            onClick={() => navigate(`/invoices/${organisationId}/create`)}
            className="btn btn-primary btn-sm gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Create Invoice
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <label className="form-control flex-1 min-w-[260px]">
            <span className="label-text text-xs text-base-content/70">Search</span>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-base-content/50" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Invoice number, company, or PO"
                className="input input-bordered input-sm w-full pl-8"
              />
            </div>
          </label>

          <label className="form-control">
            <span className="label-text text-xs text-base-content/70">From</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input input-bordered input-sm"
            />
          </label>

          <label className="form-control">
            <span className="label-text text-xs text-base-content/70">To</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input input-bordered input-sm"
            />
          </label>

          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="btn btn-ghost btn-sm gap-1"
              title="Clear all filters"
            >
              <XMarkIcon className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>

        {/* Status pills */}
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((opt) => {
            const active = statusFilter.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleStatus(opt.value)}
                aria-pressed={active}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'border-primary bg-primary text-primary-content'
                    : 'border-base-300 bg-base-200 text-base-content/80 hover:text-base-content'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="bg-base-100 shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6">
            <LoadingSkeleton rows={6} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title="No invoices"
              body={
                filtersActive
                  ? 'No invoices match the current filters.'
                  : 'No invoices have been issued for this organisation yet.'
              }
            />
          </div>
        ) : (
          <table className="table w-full">
            <thead>
              <tr>
                <th>
                  <SortHeader
                    label="Invoice Number"
                    onClick={() => toggleSort('invoiceNumber')}
                    icon={sortIcon('invoiceNumber')}
                  />
                </th>
                <th>Customer</th>
                <th>Status</th>
                <th>
                  <SortHeader
                    label="Issue Date"
                    onClick={() => toggleSort('issueDate')}
                    icon={sortIcon('issueDate')}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Due Date"
                    onClick={() => toggleSort('dueDate')}
                    icon={sortIcon('dueDate')}
                  />
                </th>
                <th>PO Number</th>
                <th className="text-right">
                  <SortHeader
                    label="Total"
                    onClick={() => toggleSort('total')}
                    icon={sortIcon('total')}
                    align="end"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <button
                      type="button"
                      onClick={() => navigate(`/invoices/${organisationId}/${invoice.id}`)}
                      className="link link-primary link-hover font-medium"
                    >
                      {invoice.invoiceNumber}
                    </button>
                  </td>
                  <td className="text-sm">{invoice.billingAddress.companyName}</td>
                  <td>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(
                        invoice.status
                      )}`}
                    >
                      {invoice.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="text-sm text-base-content/70 whitespace-nowrap">
                    {formatDate(invoice.issueDate)}
                  </td>
                  <td className="text-sm text-base-content/70 whitespace-nowrap">
                    {formatDate(invoice.dueDate)}
                  </td>
                  <td className="text-sm">{invoice.purchaseOrderNumber || '—'}</td>
                  <td className="text-right text-sm whitespace-nowrap">
                    £{invoice.total.toFixed(2)} {invoice.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <p className="text-xs text-base-content/60">
          Showing {filtered.length} of {invoices.length} invoice{invoices.length === 1 ? '' : 's'}.
        </p>
      )}
    </div>
  );
};

function SortHeader({
  label,
  onClick,
  icon,
  align,
}: {
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
  align?: 'start' | 'end';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 hover:text-base-content w-full ${
        align === 'end' ? 'justify-end' : ''
      }`}
    >
      <span>{label}</span>
      {icon}
    </button>
  );
}

export default InvoiceList;
